import { useState } from "react";
import { toast } from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "components/ui/Button";
import { Checkbox } from "components/ui/Checkbox";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { PatientAutocomplete } from "components/ui/PatientAutocomplete/PatientAutocomplete";
import { useMergePatient } from "features/patients/hooks/useMergePatient";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { formatDateOfBirth } from "utils/dateFormatter";

import styles from "./MergePatientModal.module.css";

// "PatientImmunisation" -> "Patient Immunisation" - the backend sends the DAO's
// class-derived label as-is, this is display formatting only.
const formatRecordLabel = (label) => label.replace(/([a-z0-9])([A-Z])/g, "$1 $2");

// Matches PatientAutocomplete's own option label ("Name (DD-MM-YYYY)") so the checkbox
// and the dialog's running text read the same way regardless of which side of the
// merge a patient is on.
const formatPatientBoxLabel = (p) => {
  if (!p) return "";
  const name = [p.firstName, p.surname].filter(Boolean).join(" ");
  const dob = formatDateOfBirth(p.dateOfBirth);
  return dob && dob !== "—" ? `${name} (${dob})` : name;
};

const formatPatientAddress = (p) => {
  const { addressLine1, addressLine2, city, postCode } = p?.address ?? {};
  return [addressLine1, addressLine2, city && `${city} ${postCode}`.trim()]
    .filter(Boolean)
    .join(", ");
};

// The key facts a staff member checks before confirming "yes, this is the right
// patient" - the same fields PatientCard shows, condensed. Only `patient` is
// guaranteed full detail here (it's the record the current page already loaded);
// the target patient's full record is fetched separately, see `usePatientData` below.
const getPatientCardFields = (p) => {
  if (!p) return null;
  return {
    name: [p.firstName, p.surname].filter(Boolean).join(" "),
    dateOfBirth: formatDateOfBirth(p.dateOfBirth),
    medicalCardNumber: p.medicalCardNumber || "",
    phone: p.mobilePhone || p.homePhone || "",
    email: p.email || "",
    address: formatPatientAddress(p),
  };
};

// `tone` gives each side of the merge a distinct colour so "which patient is which" is
// readable at a glance: red for the one being moved/merged away, blue for the one it's
// merging into - same pairing in the confirm step and the completed-merge summary.
const PatientMergeCard = ({ label, fields, fallbackName, tone }) => (
  <div
    className={`${styles.patientBox} ${tone === "red" ? styles.patientBoxRed : styles.patientBoxBlue}`}
  >
    <span className={styles.patientBoxLabel}>{label}</span>
    <span className={styles.patientBoxName}>{fields?.name || fallbackName}</span>
    {fields ? (
      <dl className={styles.patientBoxDetails}>
        <div className={styles.patientBoxDetailRow}>
          <dt>DOB</dt>
          <dd>{fields.dateOfBirth}</dd>
        </div>
        {fields.medicalCardNumber && (
          <div className={styles.patientBoxDetailRow}>
            <dt>Medical card</dt>
            <dd>{fields.medicalCardNumber}</dd>
          </div>
        )}
        {fields.phone && (
          <div className={styles.patientBoxDetailRow}>
            <dt>Phone</dt>
            <dd>{fields.phone}</dd>
          </div>
        )}
        {fields.email && (
          <div className={styles.patientBoxDetailRow}>
            <dt>Email</dt>
            <dd className={styles.patientBoxEllipsis}>{fields.email}</dd>
          </div>
        )}
        {fields.address && (
          <div className={styles.patientBoxDetailRow}>
            <dt>Address</dt>
            <dd>{fields.address}</dd>
          </div>
        )}
      </dl>
    ) : (
      <span className={styles.patientBoxLoading}>Loading details…</span>
    )}
  </div>
);

const STEP = { SEARCH: "search", CONFIRM: "confirm", RESULT: "result" };

// One modal for the whole flow (search -> confirm -> result), not a Dialog stacked on a
// ConfirmDialog stacked on another Dialog - the content inside changes, the modal doesn't.
const MergePatientModal = ({ open, onClose, patient }) => {
  const [step, setStep] = useState(STEP.SEARCH);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [mergeResult, setMergeResult] = useState(null);
  const [confirmed, setConfirmed] = useState(false);

  const mergePatient = useMergePatient();
  const queryClient = useQueryClient();

  // `patient` (the one being merged away) already carries full detail - it's the
  // current page's own patient. `selectedPatient` from PatientAutocomplete only has
  // {id, dateOfBirth, title}, so its address/phone/email/medical card number need a
  // separate fetch once it's picked.
  const { data: targetPatient } = usePatientData(selectedPatient?.id);

  const currentPatientName = patient
    ? [patient.firstName, patient.surname].filter(Boolean).join(" ")
    : "";
  const currentPatientBoxLabel = formatPatientBoxLabel(patient);
  const currentPatientFields = getPatientCardFields(patient);
  const targetPatientFields = getPatientCardFields(targetPatient);

  const resetAndClose = () => {
    // Only a completed merge leaves anything to refresh - queries aren't invalidated the
    // moment the API call succeeds (see useMergePatient), only once the person has actually
    // reviewed the summary and is leaving it, however they leave (OK, overlay, Escape, X).
    if (mergeResult) {
      queryClient.invalidateQueries();
    }
    setStep(STEP.SEARCH);
    setSelectedPatient(null);
    setMergeResult(null);
    setConfirmed(false);
    onClose();
  };

  // Ignored while a merge is in flight - the overlay/Escape/X paths all funnel through this,
  // so someone can't dismiss the modal mid-request and lose track of whether it went through.
  const handleDialogClose = () => {
    if (mergePatient.isPending) return;
    resetAndClose();
  };

  const handleContinue = () => {
    if (!selectedPatient) return;

    if (selectedPatient.id === patient.id) {
      toast.error("You cannot merge a patient into themselves");
      return;
    }

    setConfirmed(false);
    setStep(STEP.CONFIRM);
  };

  // Back to search - the target patient may change, so the "I understand X into Y"
  // checkbox can't carry an acknowledgement of a pairing that no longer applies.
  const handleBack = () => {
    setConfirmed(false);
    setStep(STEP.SEARCH);
  };

  const handleConfirmMerge = () => {
    mergePatient.mutate(
      { existingPatientId: patient.id, newPatientId: selectedPatient.id },
      {
        onSuccess: (data) => {
          setMergeResult(data);
          setStep(STEP.RESULT);
        },
      }
    );
  };

  const recordsUpdated = Object.entries(mergeResult?.recordsUpdated ?? {});
  const totalRecordsUpdated = recordsUpdated.reduce((sum, [, count]) => sum + count, 0);

  const titleByStep = {
    [STEP.SEARCH]: "Merge patient",
    [STEP.CONFIRM]: "Merge patient",
    [STEP.RESULT]: "Merge complete",
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} title={titleByStep[step]} width={600}>
      {step === STEP.SEARCH && (
        <>
          <div className={styles.form}>
            <p className={styles.description}>
              Merge <strong>{currentPatientName}</strong>&apos;s records into another
              patient. Every record currently linked to this patient — appointments,
              consultations, documents and more — will be moved onto the patient you
              select below, and this patient will be marked as merged. This cannot be
              undone.
            </p>
            <div className={styles.field}>
              <label className={styles.label}>Merge into</label>
              <PatientAutocomplete
                value={selectedPatient}
                onChange={setSelectedPatient}
                placeholder="Search by name..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetAndClose}>
              Cancel
            </Button>
            <Button onClick={handleContinue} disabled={!selectedPatient}>
              Continue
            </Button>
          </DialogFooter>
        </>
      )}

      {step === STEP.CONFIRM && (
        <>
          <div className={styles.form}>
            {mergePatient.isPending ? (
              <div className={styles.loadingState}>
                <Loader2 size={28} className={styles.loadingSpinner} />
                <p>Merging patient records — this may take a moment...</p>
              </div>
            ) : (
              <>
                <div className={styles.mergeCardsRow}>
                  <PatientMergeCard
                    label="Merging"
                    fields={currentPatientFields}
                    fallbackName={currentPatientName}
                    tone="red"
                  />
                  <ArrowRight
                    size={20}
                    className={styles.mergeArrow}
                    aria-hidden="true"
                  />
                  <PatientMergeCard
                    label="Into"
                    fields={targetPatientFields}
                    fallbackName={selectedPatient?.title}
                    tone="blue"
                  />
                </div>
                <p className={styles.description}>
                  Every record on this patient — appointments, consultations,
                  documents and more — will be moved onto the selected patient.
                </p>
                <label className={styles.confirmRow}>
                  <Checkbox
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                  />
                  <span>
                    I understand this will merge{" "}
                    <strong>{currentPatientBoxLabel}</strong> into{" "}
                    <strong>{selectedPatient?.title}</strong>, and this cannot be
                    undone.
                  </span>
                </label>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={mergePatient.isPending}
            >
              Back
            </Button>
            <Button
              onClick={handleConfirmMerge}
              disabled={mergePatient.isPending || !confirmed}
            >
              {mergePatient.isPending ? "Merging..." : "Merge"}
            </Button>
          </DialogFooter>
        </>
      )}

      {step === STEP.RESULT && (
        <>
          <div className={styles.form}>
            <div className={styles.mergeCardsRow}>
              <PatientMergeCard
                label="Merged"
                fields={currentPatientFields}
                fallbackName={currentPatientName}
                tone="red"
              />
              <ArrowRight size={20} className={styles.mergeArrow} aria-hidden="true" />
              <PatientMergeCard
                label="Into"
                fields={targetPatientFields}
                fallbackName={selectedPatient?.title}
                tone="blue"
              />
            </div>
            <p className={styles.description}>
              {totalRecordsUpdated} record{totalRecordsUpdated === 1 ? "" : "s"} updated:
            </p>
            <ul className={styles.resultList}>
              {recordsUpdated.length === 0 && (
                <li className={styles.resultRow}>
                  <span>No linked records were found to move</span>
                </li>
              )}
              {recordsUpdated.map(([label, count]) => (
                <li key={label} className={styles.resultRow}>
                  <span>{formatRecordLabel(label)}</span>
                  <span className={styles.resultCount}>{count}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={resetAndClose}>OK</Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
};

export { MergePatientModal };
